import {
  Button,
  DataTableToolbar,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@portals-platform/ui';
import { useNavigate } from '@tanstack/react-router';
import type { Table } from '@tanstack/react-table';
import { PlusIcon, SearchIcon } from 'lucide-react';
import type { CertificateRow } from '../../data-access/index.js';
import { ALL_STATUSES } from '../../hooks/use-certificate-list-state.js';
import { CERT_SEARCH_STATUSES, CERT_STATUS } from '../../lib/cert-status.js';

export interface CertificateToolbarProps {
  table: Table<CertificateRow>;
  searchInput: string;
  onSearchChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  isFiltered: boolean;
  onReset: () => void;
  totalElements: number;
}

export function CertificateToolbar({
  table,
  searchInput,
  onSearchChange,
  status,
  onStatusChange,
  isFiltered,
  onReset,
  totalElements,
}: CertificateToolbarProps) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col gap-3">
      <DataTableToolbar
        table={table}
        isFiltered={isFiltered}
        onReset={onReset}
        search={
          <>
            <div className="relative w-full max-w-[420px]">
              <SearchIcon
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                aria-label="Search certificates"
                placeholder="Search certificates"
                className="pl-9"
                value={searchInput}
                onChange={(event) => onSearchChange(event.target.value)}
              />
            </div>
            <Select value={status} onValueChange={onStatusChange}>
              <SelectTrigger aria-label="Filter by status" className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STATUSES}>All Statuses</SelectItem>
                {CERT_SEARCH_STATUSES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {CERT_STATUS[value].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
        actions={
          <Button onClick={() => navigate({ to: '/certificates/new' })}>
            <PlusIcon aria-hidden="true" />
            Create certificate
          </Button>
        }
      />
      {!isFiltered && (
        <p className="text-[13px] text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{totalElements}</span>{' '}
          certificates
        </p>
      )}
    </div>
  );
}
